<template>
  <div class="verify-container">
    <div class="bg-decoration bg-circle bg-circle-1"></div>
    <div class="bg-decoration bg-circle bg-circle-2"></div>
    
    <el-card class="verify-card">
      <div class="logo-wrapper">
        <el-icon class="logo-icon"><Mobile /></el-icon>
        <h2 class="logo-title">手机号验证</h2>
      </div>

      <el-form :model="verifyForm" :rules="verifyRules" ref="verifyFormRef" label-width="0px" class="verify-form">
        <el-form-item prop="phone">
          <el-input 
            v-model="verifyForm.phone" 
            placeholder="请输入手机号" 
            :prefix-icon="Mobile" 
            class="form-input" 
            :border="false" 
            autocomplete="off"
            disabled
          ></el-input>
        </el-form-item>

        <el-form-item prop="code">
          <el-input 
            v-model="verifyForm.code" 
            placeholder="请输入验证码" 
            :prefix-icon="Code" 
            class="form-input" 
            :border="false" 
            autocomplete="off"
          >
            <template #suffix>
              <el-button 
                type="text" 
                class="get-code-btn"
                :disabled="codeDisabled || !verifyForm.phone"
                @click="handleGetCode"
              >
                {{ codeText }}
              </el-button>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-button 
            type="primary" 
            @click="handleVerify" 
            class="verify-btn" 
            :loading="verifyLoading" 
            :disabled="verifyLoading"
          >
            <span v-if="!verifyLoading">验证并登录</span>
            <span v-else>验证中...</span>
          </el-button>
        </el-form-item>
      </el-form>

      <div class="copyright">© 2025 用户数据管理系统 版权所有</div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue' // 新增onUnmounted清理定时器
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Mobile, Code } from '@element-plus/icons-vue'
import axios from 'axios'

const router = useRouter()
const route = useRoute()
const verifyFormRef = ref(null)
const verifyLoading = ref(false)

// 验证码倒计时核心变量
const codeDisabled = ref(false)
const codeText = ref('获取验证码')
let countdown = 60 // 倒计时总秒数，1分钟
let timer = null // 定时器实例，用于清理

// 表单数据（手机号从路由参数获取）
const verifyForm = ref({
  phone: '',
  code: ''
})

// 表单校验规则
const verifyRules = ref({
  phone: [
    { required: true, message: '手机号不能为空', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
  ],
  code: [
    { required: true, message: '验证码不能为空', trigger: 'blur' },
    { len: 6, message: '验证码为6位数字', trigger: 'blur' },
    { pattern: /^\d{6}$/, message: '验证码只能是数字', trigger: 'blur' }
  ]
})

// 页面挂载时获取路由中的手机号
onMounted(() => {
  const phone = route.query.phone
  if (phone) {
    verifyForm.value.phone = phone
  } else {
    ElMessage.error('缺少手机号参数，返回登录页')
    setTimeout(() => router.push('/login'), 1000)
  }
})

// 倒计时函数（封装复用）
const startCountdown = () => {
  codeDisabled.value = true // 禁用按钮
  codeText.value = `${countdown}s后重新获取` // 显示倒计时
  
  // 清除旧定时器，避免重复计时
  if (timer) clearInterval(timer)
  
  // 启动倒计时
  timer = setInterval(() => {
    countdown--
    codeText.value = `${countdown}s后重新获取`
    
    // 倒计时结束，重置状态
    if (countdown <= 0) {
      clearInterval(timer)
      codeDisabled.value = false
      codeText.value = '获取验证码'
      countdown = 60 // 重置倒计时秒数
    }
  }, 1000)
}

// 获取验证码
const handleGetCode = async () => {
  try {
    // 调用获取验证码接口（传递手机号参数给后端）
    const res = await axios({
      url: 'https://api.wuxiahaojie.com/api/Admin/GetSmsCode',
      method: 'get',
      params: {
        phone: verifyForm.value.phone // 关键：传递手机号，匹配后端频率限制
      },
      timeout: 10000
    })

    if (res.data.code === 200) {
      ElMessage.success('验证码发送成功，请注意查收')
      startCountdown() // 接口成功后启动倒计时
    } else if (res.data.code === 429) {
      // 后端返回频率限制，同步启动倒计时
      ElMessage.warning(res.data.msg || '1分钟内只能获取一次验证码')
      startCountdown()
    } else {
      ElMessage.error(res.data.msg || '验证码发送失败')
    }
  } catch (error) {
    let errorMsg = '验证码发送失败'
    if (error.code === 'ERR_CONNECTION_REFUSED') {
      errorMsg = '后端服务异常，请检查服务'
    } else if (error.response?.status) {
      errorMsg = `请求失败:${error.response.status}`
    }
    ElMessage.error(errorMsg)
    console.error('获取验证码错误:', error)
  }
}

// 验证验证码并登录
const handleVerify = async () => {
  if (!verifyFormRef.value) return

  try {
    await verifyFormRef.value.validate()
    verifyLoading.value = true

    // 调用验证码验证接口
    const res = await axios({
      url: 'https://api.wuxiahaojie.com/api/Admin/VerifySmsCode',
      method: 'get',
      params: {
        Phone: verifyForm.value.phone,
        Code: verifyForm.value.code
      },
      timeout: 10000 // 删除多余的Content-Type（GET请求不需要）
    })

    if (res.data.code === 200) {
      ElMessage.success('验证成功，正在跳转到管理员页面...')
      setTimeout(() => {
        router.push('/Dashboard')
      }, 800)
    } else {
      ElMessage.error(res.data.msg || '验证码错误，请重新输入')
    }
  } catch (error) {
    let errorMsg = '验证失败'
    if (error.response?.status) {
      errorMsg = `请求失败:${error.response.status}`
    } else if (error.message === 'Network Error') {
      errorMsg = '网络异常，请检查网络'
    }
    ElMessage.error(errorMsg)
    console.error('验证验证码错误:', error)
  } finally {
    verifyLoading.value = false
  }
}

// 页面卸载时清理定时器（防止内存泄漏）
onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<style scoped>
.verify-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background: linear-gradient(120deg, #4361ee 0%, #3a0ca3 100%);
  overflow: hidden;
}

.bg-decoration {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
  z-index: 0;
}

.bg-circle-1 { width: 500px; height: 500px; top: -150px; left: -150px; }
.bg-circle-2 { width: 300px; height: 300px; bottom: -100px; right: -100px; }

.verify-card {
  position: relative;
  width: 450px;
  padding: 40px 30px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  z-index: 1;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
}

.logo-icon { font-size: 32px; color: #4361ee; margin-right: 12px; }
.logo-title { font-size: 26px; color: #2d3748; font-weight: 600; letter-spacing: 0.5px; }

.verify-form {
  width: 100%;
  gap: 16px;
  display: flex;
  flex-direction: column;
}

.form-input {
  width: 100%;
  height: 52px;
  padding: 0 20px;
  background: rgba(247, 248, 250, 0.8);
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s ease;
}

.form-input:focus {
  background: #fff;
  box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.2);
}

.get-code-btn {
  color: #4361ee !important;
  font-size: 14px;
  padding: 0 10px;
}

.get-code-btn:disabled {
  color: #718096 !important;
  cursor: not-allowed;
}

.verify-btn {
  width: 100%;
  height: 54px;
  font-size: 18px;
  font-weight: 500;
  border-radius: 12px;
  background: linear-gradient(90deg, #4361ee 0%, #3a0ca3 100%);
  border: none;
  transition: all 0.3s ease;
}

.verify-btn:hover {
  background: linear-gradient(90deg, #3a56d4 0%, #320b8f 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(67, 97, 238, 0.2);
}

.verify-btn:active { transform: translateY(0); }

.copyright {
  margin-top: 30px;
  text-align: center;
  font-size: 13px;
  color: #718096;
  letter-spacing: 0.3px;
}

@media (max-width: 576px) {
  .verify-card { width: 90%; padding: 30px 20px; }
  .logo-title { font-size: 22px; }
  .form-input { height: 48px; font-size: 15px; }
  .verify-btn { height: 50px; font-size: 16px; }
}
</style>